'use client';

import React, { useState, useEffect } from 'react';
import LandlordHero from '@/components/custom/landlord/LandlordHero';
import PlatformsSection from '@/components/custom/landlord/PlatformsSection';
import ComparisonSection from '@/components/custom/landlord/ComparisonSection';
import PropertyOwnerHero from '@/components/custom/home/PropertyOwnerHero';
import PropertyOwnerBenefits from '@/components/custom/home/PropertyOwnerBenefits';
import PropertyOwnerCTA from '@/components/custom/home/PropertyOwnerCTA';
import FAQ, { FAQItem } from '@/components/custom/home/FAQ';
import LandlordTestimonials from '@/components/custom/home/LandlordTestimonials';
import { areaAPI, hostEnquiryAPI, HostEnquiryData } from '@/lib/api';
import { toast } from 'sonner';
import { PropertyInputs, validateInputs } from '@/lib/predictionService';

const faqData: FAQItem[] = [
    {
        id: 1,
        question: "What are the requirements for renting your property?",
        answer: "We do require that all our properties shall be furnished in accordance with DTCM (Department of Tourism and Commerce Marketing) furnishing requirements and be available for at least 90 calendar days per year.\n\nOur team can help you to furnish your holiday homes or arrange the missing items in order to match the standards, simply contact info@18bricksholidayshome.ae or call us at + 97143920202 for a free consultation."
    },
    {
        id: 2,
        question: "How do I list my property?",
        answer: "If you would like to become one of our homeowners, we can arrange an initial phone consultation with our team, which is followed by a visit to your home and a bespoke proposal/quotation.\n\nOnce the terms are agreed to and an agreement is signed, we can schedule a professional photo shoot and blueprint of your home.\n\nOur registered holiday homes are typically ready to be marketed within a few days, and we can then start welcoming guests for your available dates."
    },
    {
        id: 3,
        question: "How much can I earn from my holiday home?",
        answer: "How much you can earn is dependent on your home's location, quality, and availability. If you would like to find out more, please get in touch and our team will happily organize a personalized forecast for you."
    },
    {
        id: 4,
        question: "How can I be assured that my property is safe and secure?",
        answer: "Our team can talk you through all the ways in which we keep your home safe and secure. From strict guest vetting to insurance and safety requirements for your home.\n\nEvery home should be equipped with a fire extinguisher, first aid kit and other safety equipment, be in accordance to DTCM furnishing list for holiday homes and a third party (public liability) insurance will be obtained prior to renting your property."
    },
    {
        id: 5,
        question: "What happens if something is damaged during a guest stay?",
        answer: "At 18bricks Holiday Homes™, we take care of your property as if it was our own, and that is why we have a number of processes in place to avoid any issues arising from guest vetting, collection of security deposits and insurance to physical and photo inventories as well as personal check-ins and check-outs for each guest."
    }
];

const Landlord = () => {
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        email: '',
        propertyName: '',
        furnishStatus: '',
        selectedBeds: [] as string[],
        selectedBaths: [] as string[],
        propertyType: '',
        area: '',
    });

    // Area list for getting area name
    const [areaList, setAreaList] = useState<any[]>([]);

    useEffect(() => {
        const fetchAreas = async () => {
            try {
                const resp = await areaAPI.getFrontendList();
                const raw = (resp as any)?.data;
                const list = (raw?.data ?? raw?.items ?? raw?.results ?? raw) as any[];
                setAreaList(Array.isArray(list) ? list : []);
            } catch (error) {
                console.error('Error fetching areas:', error);
            }
        };
        fetchAreas();
    }, []);

    // Get area name from ID
    const getAreaName = () => {
        const area = areaList.find(a => a._id === formData.area);
        return area?.name || formData.area || 'Not specified';
    };

    // Handle search
    const handleSearch = async () => {
        if (currentStep === 1) {
            // Validate step 1 inputs
            const inputs: PropertyInputs = {
                area: formData.area,
                propertyType: formData.propertyType,
                furnishStatus: formData.furnishStatus,
                selectedBeds: formData.selectedBeds,
                selectedBaths: formData.selectedBaths,
            };

            const validation = validateInputs(inputs);
            if (!validation.isValid) {
                toast.error(`Please complete the following: ${validation.errors.join(', ')}`);
                return;
            }
            setCurrentStep(2);
        } else if (currentStep === 2) {
            // Validate form data
            if (!formData.name.trim() || !formData.mobile.trim() || !formData.email.trim() || !formData.propertyName.trim()) {
                toast.error('Please fill in all required fields');
                return;
            }

            // Validate email format
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                toast.error('Please enter a valid email address');
                return;
            }

            // Validate mobile number
            const mobileRegex = /^[0-9]{10}$/;
            if (!mobileRegex.test(formData.mobile.replace(/\D/g, ''))) {
                toast.error('Please enter a valid 10-digit mobile number');
                return;
            }

            // Create combined payload
            const combinedPayload: HostEnquiryData = {
                name: formData.name,
                email: formData.email,
                mobile: formData.mobile,
                propertyName: formData.propertyName,
                furnishStatus: formData.furnishStatus,
                selectedBeds: formData.selectedBeds,
                selectedBaths: formData.selectedBaths,
                propertyType: formData.propertyType,
                area: formData.area,
                // type: 'property_earning_inquiry'
            };

            // Submit to API with promise toast
            const submitInquiry = async () => {
                const response = await hostEnquiryAPI.createWeb(combinedPayload);
                if (response.success) {
                    setCurrentStep(3);
                    return response;
                } else {
                    throw new Error(response.message || 'Failed to submit inquiry. Please try again.');
                }
            };

            toast.promise(submitInquiry(), {
                loading: 'Submitting your property inquiry...',
                success: 'Inquiry submitted successfully! We will get back to you soon.',
                error: (error) => error.message || 'Failed to submit inquiry. Please try again.',
            });
        }
    };

    return (
        <div>
            <LandlordHero
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
                formData={formData}
                setFormData={setFormData}
                areaList={areaList}
                handleSearch={handleSearch}
            />

            <PlatformsSection />

            <PropertyOwnerHero />

            <ComparisonSection />

            <PropertyOwnerBenefits />

            {/* <Promotions /> */}

            <LandlordTestimonials />
            <PropertyOwnerCTA />
            <FAQ faqData={faqData} />
            {/* <JourneyBanner /> */}
        </div>
    )
}

export default Landlord